window.PixiComponent = new (function (PIXI, _) {
  const pixiComponent = this;

  // 64bit floating point value that directly precedes 1.0.
  const BELOW_ONE = 0.999999940395355224609375;

  this.stage = new PIXI.Container();
  this.stage.sortableChildren = true;
  this.levelScale = 1.0;
  this.ticker = PIXI.Ticker.shared;
  this.app = null;
  this.preloadedImages = {};
  this.currentLevel = 0;
  this.currentStep = 0;
  this.pixiObjects = {};
  this.activeTabs = {};
  this.isDarkMode = false;

  this.initialize = function (options, tickerFunc) {
    if (options.backgroundColor !== undefined) {
      options.backgroundColor = stringColorToNumberColor(options.backgroundColor);
    }

    this.app = new PIXI.Application(options);
    this.app.stage = this.stage;
    this.app.stage.sortableChildren = true;
    this.app.ticker = this.ticker;

    this.ticker.add(tickerFunc);
    this.app.start();

    this.levelScale = 1.0;
  };

  this.setTweenAndRenderStep = function (subStep) {
    this.setTween(subStep);
    this.app.renderer.render(this.stage);
  };

  this.renderStep = function () {
    this.app.renderer.render(this.stage);
  };

  this.setTween = function (t) {
    this.drawLevel(this.currentStep, t);
  };

  this.setBackgroundColor = function (backgroundColor) {
    this.app.renderer.backgroundColor = stringColorToNumberColor(backgroundColor);
  };

  this.addPreloadedImage = function (loadedImage) {
    const base = new PIXI.BaseTexture(loadedImage);
    loadedImage.tex = new PIXI.Texture(base);
    this.preloadedImages[loadedImage.name] = loadedImage;
  };

  this.getPreloadedImage = function (name) {
    if (this.preloadedImages) {
      const parts = name.split('|', 2);
      const part = this.isDarkMode && parts.length > 1 ? parts[1] : parts[0];
      if (!this.preloadedImages[part]) {
        var error = 'There is no image named "' + part + '"';
        console.error(error);
        alert(error);
      }
      return this.preloadedImages[part];
    }
  };

  this.setNormalScale = function () {
    this.setScaleAndResolution(1.0);
  };

  this.setDoubleScale = function () {
    this.setScaleAndResolution(2.0);
  };

  this.setScaleAndResolution = function (scale, resolution) {
    if (typeof scale === 'number') this.levelScale = scale;
    if (typeof resolution === 'number') this._pixelsPerUnit = resolution;
    this.stage.scale.set(this._pixelsPerUnit * this.levelScale);
  };

  this.clearPixiObjects = function () {
    for (var name in this.pixiObjects) {
      this.pixiObjects[name].destroy();
    }
    this.pixiObjects = {};
  };

  this.loadLevelPixiObjects = function (levelIndex) {
    this.clearPixiObjects();
    const startTime = Date.now();
    this.pixiObjects = this._createLevelObjects(this.processedLevels[levelIndex]);
    const endTime = Date.now();
    console.info('Pixi objects construction time: ' + (endTime - startTime).toString() + 'ms');
  };

  this.preprocessLevelOutput = function (levels, pixelsPerUnit) {
    this.setScaleAndResolution(null, pixelsPerUnit);
    const a = Date.now();
    this.clearPixiObjects();
    this.processedLevels = _.map(levels, this._preprocessLevel);
    this.levelStepCounts = _.map(levels, function (level) {
      return level.steps.length;
    });
    const b = Date.now();
    console.info('Preprocess time: ' + (b - a).toString() + 'ms');
    this.loadLevelPixiObjects(this.currentLevel);
    const c = Date.now();
    console.info('Total time: ' + (c - a).toString() + 'ms');
  };

  function AnimationTrack() {
    this._track = [];
    this._head = 0; // Index into _track set by seek().
  }

  AnimationTrack.prototype.getKeyframeValue = function (i) {
    return this._track[i * 2 + 1];
  };

  AnimationTrack.prototype.getKeyframeTime = function (i) {
    return this._track[i * 2];
  };

  AnimationTrack.prototype.addKeyframe = function (time, value) {
    this._track.push(time);
    this._track.push(value);
  };

  AnimationTrack.prototype.getLastKeyframeValue = function () {
    return this.getKeyframeValue(this.getKeyframeCount() - 1);
  };

  AnimationTrack.prototype.getLastKeyframeTime = function () {
    return this.getKeyframeTime(this.getKeyframeCount() - 1);
  };

  AnimationTrack.prototype.getKeyframeCount = function () {
    console.assert(this._track.length % 2 === 0);
    return this._track.length / 2;
  };

  AnimationTrack.prototype.seek = function (time) {
    // Binary search.
    //  O(log n), e.g. one additional step required for each doubling of n.
    var lo = 0;
    var hi = this.getKeyframeCount();
    function getMid() {
      return lo + Math.floor((hi - lo) / 2);
    }
    var mid = getMid();
    var count = 0;
    while (mid !== lo && count < 100) {
      count++;
      if (time <= this.getKeyframeTime(mid)) {
        hi = mid;
      } else if (time > this.getKeyframeTime(mid)) {
        lo = mid;
      } else break;
      mid = getMid();
    }
    this._head = mid;
  };

  AnimationTrack.prototype.getKeyframeAtHead = function () {
    return [this.getKeyframeValue(this._head), this.getKeyframeTime(this._head)];
  };

  AnimationTrack.prototype.getKeyframeValueAtHead = function () {
    return this.getKeyframeValue(this._head);
  };

  AnimationTrack.prototype.getKeyframeAfterHead = function () {
    if (this._head + 1 < this.getKeyframeCount()) {
      return [this.getKeyframeValue(this._head + 1), this.getKeyframeTime(this._head + 1)];
    } else {
      return this.getKeyframeAtHead();
    }
  };

  function Animation(type, name, creationStep, trackNames) {
    this.type = type;
    this.name = name;
    this.creationStep = creationStep;
    this.destructionStep = null;
    this.trackNames = trackNames;
    for (var i = 0; i < this.trackNames.length; ++i) {
      const trackName = this.trackNames[i];
      this[trackName] = new AnimationTrack();
    }
  }

  Animation.prototype.getStartState = function () {
    const state = {};
    for (var key in this) {
      if (this[key] instanceof AnimationTrack) {
        state[key] = this[key].getKeyframeValue(0);
      } else {
        state[key] = this[key];
      }
    }
    return state;
  };

  function addUpdateToAnimation(stepIndex, animation, updates) {
    for (var i = 0; i < animation.trackNames.length; ++i) {
      const key = animation.trackNames[i];
      if (updates[key] !== undefined) {
        var sequence = updates[key];
        if (!(sequence instanceof Array)) {
          sequence = [[sequence, 1.0]];
        }
        if (key === 'color') {
          // eslint-disable-next-line
          sequence = _.map(sequence, function (k) {
            return [stringColorToNumberColor(k[0]), k[1]];
          });
        }
        // eslint-disable-next-line
        sequence.forEach(function (keyframe) {
          const value = keyframe[0];
          const time = stepIndex + Math.min(BELOW_ONE, keyframe[1] || 0.0);
          const hasKeyframes = animation[key].getKeyframeCount() > 0;
          const sameValueAsPrevious = animation[key].getLastKeyframeValue() === value;
          const previousIsSameStep = fract(time) === fract(animation[key].getLastKeyframeTime());
          const skip = !previousIsSameStep && sequence.length === 1 && sameValueAsPrevious;
          if (!hasKeyframes || !skip) {
            animation[key].addKeyframe(time, value);
          }
        });
      }
    }
  }

  // prettier-ignore
  const commonAnimationTrackNames = ['x', 'y', 'scaleX', 'scaleY', 'zIndex', 'color', 'alpha', 'rotation', 'pivotX', 'pivotY', 'parent', 'hover_effect', 'toggle_on_click', 'toggle_on_hover'];
  const staticProperties = ['points', 'callbacks', 'frames', 'image', 'width', 'height', 'radius', 'edge'];
  const textTrackNames = commonAnimationTrackNames.concat(['text', 'size', 'textAlign']);

  this._preprocessLevel = function (level) {
    const animations = {};
    for (var stepIndex = 0; stepIndex < level.steps.length; stepIndex++) {
      const step = level.steps[stepIndex];
      for (var updateIndex = 0; updateIndex < step.graphics.length; updateIndex++) {
        const update = step.graphics[updateIndex];
        const id = update.name;
        if (update.type !== undefined) {
          // Only first update package for a gfx object will have this.
          var trackNames;
          switch (update.type) {
            case 'text':
              trackNames = textTrackNames;
              break;
            default:
              trackNames = commonAnimationTrackNames;
          }
          animations[id] = new Animation(update.type, id, stepIndex, trackNames);
          for (var i = 0; i < staticProperties.length; i++) {
            animations[id][staticProperties[i]] = update.updates[staticProperties[i]];
          }
          if (_.contains(['spritesheet', 'sprite'], update.type)) {
            const sheetImage = pixiComponent.getPreloadedImage(update.updates.image);
            const frameCount = sheetImage.sheetData.animations[update.updates.animation].length;
            animations[id].frames = _.map(_.range(frameCount), function (i) {
              return [i, (i + 1) / frameCount];
            });
          }
          if (update.updates.parent === undefined) {
            update.updates.parent = 'root'; // TODO: Add parent to all init updates on server.
          }
        }
        if (update.updates.destroy) {
          // TODO: Error check. Here or validate in python?
          // Could potentially be set multiple times for the same object.
          animations[id].destructionStep = stepIndex;
          continue;
        }
        addUpdateToAnimation(stepIndex, animations[id], update.updates);

        if (update.updates['callbacks'] !== undefined) {
          animations[id].callbacks = update.updates['callbacks'];
        }
      }
    }
    return animations;
  };

  this._createLevelObjects = function (processedLevel) {
    const pixiObjects = {};
    for (var name in processedLevel) {
      const animation = processedLevel[name];
      const startState = animation.getStartState();
      pixiObjects[animation.name] = pixiComponent._createPixiObject(startState.type, startState);
    }
    return pixiObjects;
  };

  this._createPixiObject = function (type, gfxUpdate) {
    const startState = gfxUpdate;
    var child;
    switch (type) {
      case 'polygon':
        child = new PIXI.Graphics();
        if (startState.edge) {
          child.lineStyle(startState.edge, 0xffffff, 1);
        } else {
          child.beginFill(0xffffff);
        }
        child.drawPolygon(startState.points);
        child.endFill();
        break;

      case 'rectangle':
        child = new PIXI.Graphics();
        if (startState.edge) {
          child.lineStyle(startState.edge, 0xffffff, 1);
        } else {
          child.beginFill(0xffffff);
        }
        child.drawRoundedRect(
          -startState.width / 2,
          -startState.height / 2,
          startState.width,
          startState.height,
          startState.radius || 0
        );
        child.endFill();
        break;

      case 'circle':
        child = new PIXI.Graphics();
        if (startState.edge) {
          child.lineStyle(startState.edge, 0xffffff, 1);
        } else {
          child.beginFill(0xffffff);
        }
        const segment_length = 2.0 / this._pixelsPerUnit;
        const segments = Math.min(512, Math.floor((startState.radius * 2.0 * Math.PI) / segment_length));
        const polygon = new PIXI.Polygon();
        const segment_angle = (Math.PI * 2.0) / segments;
        for (var i = 0; i < segments; ++i) {
          const a = i * segment_angle;
          polygon.points.push(Math.cos(a) * startState.radius);
          polygon.points.push(Math.sin(a) * startState.radius);
        }
        polygon.closeStroke = true;
        child.drawPolygon(polygon);
        child.endFill();
        break;

      case 'bitmap':
        const image = this.getPreloadedImage(startState.image);
        child = new PIXI.Sprite(image.tex);
        child.width = startState.width * startState.scaleX;
        child.height = startState.height * startState.scaleY;
        child.anchor.x = 0.5;
        child.anchor.y = 0.5;
        break;

      case 'sprite':
        const frames = [];
        var frameIndex;
        for (frameIndex = 0; frameIndex < startState.frames; frameIndex++) {
          const n = startState.image.lastIndexOf('.png');
          const name = startState.image.substring(0, n) + '_' + (frameIndex + 1) + startState.image.substring(n);
          const image = this.getPreloadedImage(name);
          frames.push(image.tex);
        }
        child = new PIXI.AnimatedSprite(frames);
        child.width = startState.width * startState.scaleX;
        child.height = startState.height * startState.scaleY;
        child.anchor.x = 0.5;
        child.anchor.y = 0.5;
        break;

      case 'spritesheet':
        const sheetImage = this.getPreloadedImage(startState.image);
        if (!sheetImage) {
          // TODO: Error handling?
          break;
        }
        const spriteSheetTexture = sheetImage.tex.baseTexture;
        const sheetData = sheetImage.sheetData;
        const animationName = startState.animation;
        var frameOrder =
          sheetData.animations[animationName] || sheetData.animations[Object.keys(sheetData.animations)[0]];

        // Set none default frames
        if (startState.frameIndexStart !== undefined || startState.frameIndexEnd !== undefined) {
          const frameStart = startState.frameIndexStart || 0;
          const frameEnd = startState.frameIndexEnd !== undefined ? startState.frameIndexEnd : frameOrder.length;
          if (frameStart <= frameEnd) {
            frameOrder = frameOrder.slice(frameStart, frameEnd + 1);
          } else {
            // Reverse animation
            frameOrder = frameOrder.slice(frameEnd, frameStart + 1);
            frameOrder.reverse();
          }
        }

        // Get individual textures from spritesheet
        const textures = [];
        for (frameIndex = 0; frameIndex < frameOrder.length; frameIndex++) {
          const spriteData = sheetData.frames[frameOrder[frameIndex]];
          if (spriteData.trimmed) {
            textures.push(
              new PIXI.Texture(
                spriteSheetTexture,
                new PIXI.Rectangle(spriteData.frame.x, spriteData.frame.y, spriteData.frame.w, spriteData.frame.h), // frame
                new PIXI.Rectangle(0, 0, spriteData.sourceSize.w, spriteData.sourceSize.h), // original size
                new PIXI.Rectangle(
                  spriteData.spriteSourceSize.x,
                  spriteData.spriteSourceSize.y,
                  spriteData.spriteSourceSize.w,
                  spriteData.spriteSourceSize.h
                )
              )
            );
          } else {
            textures.push(
              new PIXI.Texture(
                spriteSheetTexture,
                new PIXI.Rectangle(spriteData.frame.x, spriteData.frame.y, spriteData.frame.w, spriteData.frame.h)
              )
            );
          }
        }
        child = new PIXI.AnimatedSprite(textures);
        child.width = startState.width * startState.scaleX;
        child.height = startState.height * startState.scaleY;
        child.anchor.x = 0.5;
        child.anchor.y = 0.5;
        break;

      case 'text':
        /* The font tends to get squished at very small sizes, in around 1.0.
           To avoid this we're rendering it at a larger size and then scale it
           down via custom properties on the scale object. */
        const resolution = this._pixelsPerUnit;
        const fontSize = startState.size * resolution;
        const fontFamily = 'Roboto, sans-serif';
        child = new PIXI.Text(startState.text, {
          fontSize: fontSize,
          fontFamily: fontFamily,
          fill: 0xffffff,
          align: startState.textAlign,
        });
        child.anchor.x = startState.textAlign === 'left' ? 0 : startState.textAlign === 'center' ? 0.5 : 1;
        child.anchor.y = 0.5;
        child.resolution = 2.0;
        function scaledPoint(point, scale) {
          this._point = point;
          this._scale = scale;
          Object.defineProperty(this, 'x', {
            get: function () {
              return this._point.x * this._scale;
            },
            set: function (value) {
              this._point.x = value / this._scale;
            },
          });
          Object.defineProperty(this, 'y', {
            get: function () {
              return this._point.y * this._scale;
            },
            set: function (value) {
              this._point.y = value / this._scale;
            },
          });
          this.set = function (x, y) {
            this._point.set(x / this._scale, y / this._scale);
          };
        }
        child._facadeScale = new scaledPoint(child.scale, resolution);
        Object.defineProperty(child, 'scale', {
          get: function () {
            return this._facadeScale;
          },
          set: function (point) {
            this._facadeScale.set(point.x, point.y);
          },
        });
        break;

      default:
        return;
    }
    child.name = startState.name;
    return child;
  };

  function getInterpolatedTrackValue(track, stepIndex, time, interpolate) {
    const start = track.getKeyframeAtHead();
    const end = track.getKeyframeAfterHead();
    const startStep = Math.floor(start[1]);
    const endStep = Math.floor(end[1]);
    if (startStep < stepIndex) {
      // Continue value from previous step from 0.0 on this step.
      start[1] = stepIndex;
    }
    if (stepIndex < endStep) {
      // The next keyframe is in a following step, so no interpolation yet.
      return start[0];
    } else if (start[0] === end[0]) {
      return start[0];
    } else {
      const timeDiff = end[1] - start[1];
      if (timeDiff === 0.0) {
        return end[0];
      } else {
        return interpolate(start[0], end[0], (time - fract(start[1])) / timeDiff);
      }
    }
  }

  this.drawLevel = function (stepIndex, time) {
    const levelIndex = this.currentLevel;
    const level = this.processedLevels[levelIndex];
    const stage = this.stage;

    function getPixiObject(p) {
      return (p === 'root' && stage) || pixiComponent.pixiObjects[p];
    }

    function setParent(child, parent_id) {
      const parent = getPixiObject((parent_id === undefined && 'root') || parent_id);
      if (Object.is(child.parent, parent)) return;
      if (child.parent) child.parent.removeChild(child);
      parent.addChild(child);
    }

    function evaluateAnimationTracks(pixiObject, trackName, value, saveOldState) {
      var old;
      switch (trackName) {
        case 'color':
          old = pixiObject.tint;
          pixiObject.tint = value;
          break;
        case 'scaleX':
          old = pixiObject.scale.x;
          pixiObject.scale.x = value;
          break;
        case 'scaleY':
          old = pixiObject.scale.y;
          pixiObject.scale.y = value;
          break;
        case 'pivotX':
          old = pixiObject.pivot.x;
          pixiObject.pivot.x = value;
          break;
        case 'pivotY':
          old = pixiObject.pivot.y;
          pixiObject.pivot.y = value;
          break;
        case 'parent':
          old = pixiObject.parent;
          setParent(pixiObject, value);
          break;
        case 'rotation':
          old = pixiObject.angle;
          pixiObject.angle = value;
          break;
        case 'style':
          old = pixiObject.style;
          pixiObject.style = value;
          break;
        default:
          old = pixiObject[trackName];
          pixiObject[trackName] = value;
          break;
      }
      if (saveOldState && old !== value) {
        if (!pixiObject.oldState) pixiObject.oldState = {};
        pixiObject.oldState[trackName] = old;
      }
    }

    function mouseOver(pixiObject) {
      var effects = pixiObject.hover_effect;
      for (var trackName in effects) {
        if (effects.hasOwnProperty(trackName) && typeof effects[trackName] !== 'undefined') {
          var value = effects[trackName];
          if (trackName === 'color') {
            value = stringColorToNumberColor(value);
          }
          evaluateAnimationTracks(pixiObject, trackName, value, true);
        }
      }
      if (pixiObject.children)
        for (var index in pixiObject.children) if (pixiObject.children[index]) mouseOver(pixiObject.children[index]);
      if (pixiObject.hoverObjects) for (var key in pixiObject.hoverObjects) pixiObject.hoverObjects[key].alpha = 1;
    }

    function mouseOut(pixiObject) {
      var effects = pixiObject.hover_effect;
      for (var trackName in effects) {
        if (!pixiObject.oldState) break;
        var value = pixiObject.oldState[trackName];
        if (trackName === 'style') value = {};
        if (effects.hasOwnProperty(trackName) && typeof value !== 'undefined') {
          evaluateAnimationTracks(pixiObject, trackName, value, false);
        }
      }
      if (pixiObject.children)
        for (var index in pixiObject.children) if (pixiObject.children[index]) mouseOut(pixiObject.children[index]);
      if (pixiObject.hoverObjects) for (var key in pixiObject.hoverObjects) pixiObject.hoverObjects[key].alpha = 0;
    }

    for (var name in level) {
      const pixiObject = this.pixiObjects[name];
      const animation = level[name];
      if (
        stepIndex < animation.creationStep ||
        (animation.destructionStep !== null && animation.destructionStep <= stepIndex)
      ) {
        if (pixiObject.parent) {
          pixiObject.parent.removeChild(pixiObject);
        }
        continue;
      }
      if (animation.frames) {
        for (var i = 0; i <= animation.frames.length && animation.frames[i][1] < time; ++i);
        pixiObject.gotoAndStop(i - 1);
      }
      // eslint-disable-next-line
      animation.trackNames.forEach(function (trackName) {
        const track = animation[trackName];
        var value;
        track.seek(stepIndex + time);
        if (_.contains(['text', 'parent'], trackName)) {
          value = track.getKeyframeValueAtHead();
        } else {
          var interpolate = lerp;
          if (trackName === 'color') {
            interpolate = colorInterpolator(interpolate);
          }
          value = getInterpolatedTrackValue(track, stepIndex, time, interpolate);
        }
        if (_.contains(['bitmap', 'sprite', 'spritesheet'], animation.type)) {
          if (trackName === 'scaleX') {
            value = (value * animation.width) / pixiObject.texture.width;
          } else if (trackName === 'scaleY') {
            value = (value * animation.height) / pixiObject.texture.height;
          }
        }
        console.assert(
          !(typeof value === 'number' && isNaN(value)),
          'Animation track ' + name + '.' + trackName + ' resulted in NaN.'
        );
        evaluateAnimationTracks(pixiObject, trackName, value, false);
      });
      pixiObject.interactive = false;
      pixiObject.buttonMode = false;
      pixiObject.off('pointertap', onClick);
      const isLastStep = stepIndex === this.levelStepCounts[levelIndex] - 2;
      if (commandHandler && isLastStep && animation.callbacks) {
        pixiObject.interactive = true;
        if (animation.callbacks.indexOf('click') !== -1) {
          pixiObject.buttonMode = true;
          pixiObject.on('pointertap', onClick);
        }
      }

      if (pixiObject.hover_effect && pixiObject.interactive) {
        pixiObject.pointerover = function () {
          mouseOver(pixiObject);
        };
        pixiObject.pointerout = function () {
          mouseOut(pixiObject);
        };
      }

      if (pixiObject.toggle_on_hover) {
        const toggleOnHoverObject = getPixiObject(pixiObject.toggle_on_hover);
        if (toggleOnHoverObject) {
          if (!toggleOnHoverObject.hoverObjects) toggleOnHoverObject.hoverObjects = {};
          toggleOnHoverObject.hoverObjects[pixiObject.name] = pixiObject;
          toggleOnHoverObject.pointerover = function () {
            mouseOver(toggleOnHoverObject);
          };
          toggleOnHoverObject.pointerout = function () {
            mouseOut(toggleOnHoverObject);
          };
        }
      }

      if (pixiObject.toggle_on_click) {
        const toggleOnClickButton = getPixiObject(pixiObject.toggle_on_click);
        if (toggleOnClickButton) {
          toggleOnClickButton.interactive = true;
          toggleOnClickButton.buttonMode = true;
          toggleOnClickButton.pointertap = function () {
            toggleButtonOnClick(pixiObject);
          };
          toggleOnClickButton.pointerover = function () {
            mouseOver(toggleOnClickButton);
          };
          toggleOnClickButton.pointerout = function () {
            mouseOut(toggleOnClickButton);
          };
        }
      }
    }

    this.syncCurrentTabs();
  };

  // Override alpha set by animation for elements toggled with toggle_on_click
  this.syncCurrentTabs = function () {
    for (var tabGroupName in this.activeTabs) {
      if (this.pixiObjects[this.activeTabs[tabGroupName]]) {
        // toggle other components in group (tab group)
        if (this.pixiObjects[this.activeTabs[tabGroupName]].parent) {
          this.pixiObjects[this.activeTabs[tabGroupName]].parent.children.forEach(function (child) {
            if (child.toggle_on_click && child.alpha !== 0) {
              child.alpha = 0;
            }
          });
        }
        this.pixiObjects[this.activeTabs[tabGroupName]].alpha = 1;
      }
    }
  };

  this.setLevelAndStep = function (level, step) {
    if (this.currentLevel !== level) {
      this.stage.removeChildren();
      this.loadLevelPixiObjects(level);
    }
    this.currentLevel = level;
    this.currentStep = step;
  };

  var stringColorToNumberColor = (function (self) {
    // This canvas is used to convert CSS color values (hex, rgb or name).
    // There is unfortunately no more direct way to query the CSS engine.
    var ctx = document.createElement('canvas').getContext('2d');
    return function (str) {
      if (typeof str !== 'string') {
        return str;
      }
      const parts = str.split('|', 2);
      ctx.fillStyle = self.isDarkMode && parts.length > 1 ? parts[1] : parts[0];
      return parseInt(ctx.fillStyle.substring(1), 16);
    };
  })(this);

  function splitRGB(colorInt) {
    return [colorInt & 0xff, (colorInt >>> 8) & 0xff, (colorInt >>> 16) & 0xff];
  }

  function joinRGB(r, g, b) {
    return Math.trunc(r) | (Math.trunc(g) << 8) | (Math.trunc(b) << 16);
  }

  /* Decorator that splits a color into components and applies the interpolation to each. */
  function colorInterpolator(interpolate) {
    return function (start, end, time) {
      const startRGB = splitRGB(start);
      const endRGB = splitRGB(end);
      return joinRGB(
        interpolate(startRGB[0], endRGB[0], time),
        interpolate(startRGB[1], endRGB[1], time),
        interpolate(startRGB[2], endRGB[2], time)
      );
    };
  }

  /* Linear interpolation. */
  function lerp(start, end, time) {
    return start + time * (end - start);
  }

  function fract(num) {
    return num - Math.floor(num);
  }

  var commandHandler = null;
  this.setCommandHandler = function (handler) {
    commandHandler = handler;
  };

  // Event handlers, 'this' is the pixi object.
  function onClick() {
    if (!commandHandler) return;
    commandHandler({
      type: 'callback',
      name: this.name,
      action: 'click',
    });
  }

  function toggleButtonOnClick(container) {
    // toggle other components in group (tab group)
    if (container.parent) {
      window.PixiComponent.activeTabs[container.parent.name] = container.name;
      container.parent.children.forEach(function (child) {
        if (child.toggle_on_click && child.alpha !== 0) child.alpha = 0;
      });
    }
    container.alpha = 1;
  }
})(window.PIXI, window._);
